import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Option, useResponsive } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { toNumber } from 'lodash';

import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { useDocumentOcrStore, useMyMoneyFormStore } from '@/modules/UseMyMoney/stores';
import { ClaimSummaryField, UseMyMoneyOption } from '@/modules/UseMyMoney/UseMyMoney.types';
import { formatCurrency } from '@/utils';

interface Props {
  useMyMoneyType?: UseMyMoneyOption;
  hasAvailableAccounts?: boolean;
}

const useClaimSummaryFields = ({ useMyMoneyType, hasAvailableAccounts }: Props) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { values } = useMyMoneyFormStore();
  const { ocrDocument } = useDocumentOcrStore();

  const allFields = useMemo((): Option[] => {
    if (
      useMyMoneyType === UseMyMoneyOption.REIMBURSE_MYSELF ||
      useMyMoneyType === UseMyMoneyOption.PAY_A_PROVIDER
    ) {
      const provider = ocrDocument?.providers?.find((provider) => provider?.id === values?.providerId);
      const patient = ocrDocument?.serviceForOptions?.find((patient) => patient?.id === values?.patientId);
      const service = ocrDocument?.serviceOptions?.find((service) => service?.serviceId === values?.serviceId);

      return [
        {
          key: ClaimSummaryField.AMOUNT,
          title: t('Amount'),
          value: values?.amount ? formatCurrency(toNumber(values?.amount)) : undefined,
        },
        {
          key: ClaimSummaryField.PROVIDER,
          title: t('Retailer/ Provider'),
          value: provider?.name && (provider?.address?.line1 || useMyMoneyType === UseMyMoneyOption.REIMBURSE_MYSELF)
            ? provider?.name
            : '',
        },
        {
          key: ClaimSummaryField.SERVICE_FOR,
          title: t('Service For'),
          value: patient?.fullName,
        },
        {
          key: ClaimSummaryField.DATE_OF_SERVICE,
          title: t('Date of Service'),
          value: values?.dateOfService && hasAvailableAccounts
            ? dayjs(values?.dateOfService).format(DEFAULT_DATE_FORMAT)
            : undefined,
        },
        {
          key: ClaimSummaryField.SERVICE_DESCRIPTION,
          title: isMobile ? t('Service') : t('Service Description'),
          ...hasAvailableAccounts
            ? {}
            : { description: t(`The previously selected service is not covered by the date of service selected, ${dayjs(values?.dateOfService).format(DEFAULT_DATE_FORMAT)}. Please select a new service.`) },
          value: service?.serviceName,
        },
      ];
    }

    if (useMyMoneyType === UseMyMoneyOption.HSA_WITHDRAWAL) {
      return [
        {
          key: ClaimSummaryField.AMOUNT,
          title: t('Amount'),
          value: values?.amount ? formatCurrency(toNumber(values?.amount)) : undefined,
        },
      ];
    }

    return [];
  }, [useMyMoneyType, t, isMobile, values, ocrDocument, hasAvailableAccounts]);

  const filledFields = useMemo(() => allFields?.filter((field) => field?.value), [allFields]);
  const emptyFields = useMemo(() => allFields?.filter((field) => !field?.value), [allFields]);

  return {
    allFields,
    filledFields,
    hasFilledFields: filledFields?.length > 0,
    emptyFields,
    hasEmptyFields: emptyFields?.length > 0,
  };
};

export default useClaimSummaryFields;
